// src/pages/Home/About.jsx

import React from "react";
import {
  FaArrowRight,
  FaCheck,
  FaEnvelope,
  FaExternalLinkAlt,
  FaPhoneAlt,
} from "react-icons/fa";

import Reveal from "../../components/ui/Reveal";

import {
  aboutAudiences,
  aboutCommitments,
  aboutProfile,
  aboutServices,
} from "./data/aboutData";

const cx = (...classes) => classes.filter(Boolean).join(" ");

function ContactLink({ href, icon: Icon, label, value }) {
  return (
    <a
      href={href}
      className={cx(
        "group flex min-w-0 items-center gap-3 py-4",
        "border-b border-slate-200 last:border-b-0",
        "transition-colors hover:text-[#0868f2]",
        "dark:border-white/10",
      )}
    >
      <span
        className={cx(
          "grid h-9 w-9 shrink-0 place-items-center rounded-full",
          "bg-blue-50 text-[#0868f2]",
          "transition-colors group-hover:bg-[#0868f2] group-hover:text-white",
          "dark:bg-blue-500/10 dark:text-blue-300",
        )}
      >
        <Icon className="text-sm" />
      </span>

      <span className="min-w-0">
        <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </span>

        <span className="mt-1 block truncate text-sm font-semibold text-slate-900 transition-colors group-hover:text-[#0868f2] dark:text-white dark:group-hover:text-blue-300">
          {value}
        </span>
      </span>

      <FaArrowRight className="ml-auto shrink-0 text-[10px] text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-[#ff7900]" />
    </a>
  );
}

function ServiceVisualCard({ service }) {
  const Icon = service.icon;

  return (
    <article
      className={cx(
        "group overflow-hidden rounded-[22px]",
        "border border-slate-200 bg-white",
        "transition duration-500",
        "hover:-translate-y-1 hover:shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)]",
        "dark:border-white/10 dark:bg-slate-950",
      )}
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-200 dark:bg-slate-800">
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.035]"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />

        <div className="absolute left-5 top-5">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-slate-950/45 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-md">
            <Icon className="text-[#ff7900]" />
            {service.eyebrow}
          </span>
        </div>
      </div>

      {/* Contenu lisible */}
      <div className="p-6 sm:p-7">
        <h3 className="max-w-md text-2xl font-semibold leading-tight tracking-[-0.025em] text-slate-950 dark:text-white">
          {service.title}
        </h3>

        <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
          {service.description}
        </p>

        <div className="mt-6 border-t border-slate-200 pt-5 dark:border-white/10">
          <div className="space-y-3">
            {service.points.map((point) => (
              <div
                key={point}
                className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300"
              >
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                  <FaCheck className="text-[8px]" />
                </span>

                <span>{point}</span>
              </div>
            ))}
          </div>
        </div>

        <a
          href="#contact"
          className="group/link mt-7 inline-flex items-center gap-3 text-sm font-semibold text-[#0868f2] transition hover:text-[#0648b8] dark:text-blue-300"
        >
          Échanger sur votre projet
          <span className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 transition group-hover/link:border-[#0868f2] group-hover/link:bg-[#0868f2] group-hover/link:text-white dark:border-white/10">
            <FaArrowRight className="text-[9px]" />
          </span>
        </a>
      </div>
    </article>
  );
}

function CommitmentItem({ item, index }) {
  const Icon = item.icon;

  return (
    <article
      className={cx(
        "px-0 py-7 sm:px-6 lg:px-7 lg:py-9",
        index > 0 &&
          "border-t border-slate-200 dark:border-white/10 sm:border-l sm:border-t-0",
      )}
    >
      <span
        className={cx(
          "grid h-11 w-11 place-items-center rounded-full",
          "bg-white text-[#0868f2] shadow-sm",
          "dark:bg-slate-900 dark:text-blue-300",
        )}
      >
        <Icon />
      </span>

      <h3 className="mt-5 text-lg font-semibold tracking-tight text-slate-950 dark:text-white">
        {item.title}
      </h3>

      <p className="mt-2 text-sm leading-7 text-slate-500 dark:text-slate-400">
        {item.text}
      </p>
    </article>
  );
}

export default function About() {
  return (
    <section
      id="about"
      className="scroll-mt-24 overflow-hidden bg-[#f3f6f5] py-20 dark:bg-slate-900 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid gap-8 border-b border-slate-300/70 pb-10 dark:border-white/10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0868f2] dark:text-blue-300">
                À propos de MonAppliClub
              </p>

              <h2 className="mt-5 max-w-4xl text-4xl font-semibold leading-[1.08] tracking-[-0.04em] text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Un développeur indépendant,
                <span className="block text-[#0868f2] dark:text-blue-300">
                  proche de vos réalités.
                </span>
              </h2>
            </div>

            <div className="max-w-xl lg:justify-self-end">
              <p className="text-base leading-8 text-slate-600 dark:text-slate-300">
                Je conçois des sites web et des applications pour les clubs
                sportifs, associations, collectivités, entreprises, artisans et
                projets particuliers.
              </p>

              <p className="mt-3 text-base leading-8 text-slate-600 dark:text-slate-300">
                Mon expérience de président de club, enseignant et bénévole me
                permet de créer des outils réellement adaptés au terrain.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-10 xl:grid-cols-[0.78fr_1.22fr] xl:gap-14">
          <Reveal>
            <div className="xl:sticky xl:top-28">
              <div className="relative overflow-hidden rounded-[22px] bg-slate-950">
                <div className="aspect-[4/5] sm:aspect-[5/4] xl:aspect-[4/5]">
                  <img
                    src={aboutProfile.photo}
                    alt={aboutProfile.name}
                    className="h-full w-full object-cover object-top"
                    loading="lazy"
                  />
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />

                <div className="absolute left-5 top-5">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white backdrop-blur-md">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-70" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                    </span>

                    {aboutProfile.availability}
                  </div>
                </div>

                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a8e5cb]">
                    {aboutProfile.company}
                  </p>

                  <h3 className="mt-2 text-3xl font-semibold tracking-[-0.03em] text-white">
                    {aboutProfile.name}
                  </h3>

                  <p className="mt-2 text-sm font-medium text-white/80">
                    {aboutProfile.role}
                  </p>

                  <p className="mt-1 text-xs text-white/55">
                    {aboutProfile.location}
                  </p>
                </div>
              </div>

              <div className="mt-5 border-y border-slate-300/70 dark:border-white/10">
                <ContactLink
                  href={aboutProfile.phoneHref}
                  icon={FaPhoneAlt}
                  label="Téléphone"
                  value={aboutProfile.phoneDisplay}
                />

                <ContactLink
                  href={aboutProfile.emailHref}
                  icon={FaEnvelope}
                  label="Email"
                  value={aboutProfile.email}
                />
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row xl:flex-col 2xl:flex-row">
                <a
                  href="#contact"
                  className={cx(
                    "group inline-flex h-12 flex-1 items-center justify-center gap-3 rounded-full",
                    "bg-[#0868f2] px-5 text-sm font-semibold text-white py-2",
                    "transition hover:bg-[#0648b8]",
                  )}
                >
                  Me contacter
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-[#ff7900]">
                    <FaArrowRight className="text-[9px]" />
                  </span>
                </a>

                <a
                  href={aboutProfile.vkardLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cx(
                    "inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full",
                    "border border-slate-300 px-5 text-sm font-semibold text-slate-800",
                    "transition hover:border-[#0868f2] hover:text-[#0868f2]",
                    "dark:border-white/15 dark:text-white dark:hover:border-blue-300 dark:hover:text-blue-300 py-2",
                  )}
                >
                  Carte de visite
                  <FaExternalLinkAlt className="text-[10px]" />
                </a>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#ff7900]">
                  Une approche humaine et personnalisée
                </p>

                <h3 className="mt-4 text-3xl font-semibold leading-tight tracking-[-0.03em] text-slate-950 dark:text-white sm:text-4xl">
                  De la première idée jusqu’à la prise en main de votre
                  application.
                </h3>

                <p className="mt-5 text-base leading-8 text-slate-600 dark:text-slate-300">
                  Chaque projet commence par l’écoute de votre fonctionnement,
                  de vos contraintes et de vos utilisateurs. L’objectif n’est
                  pas d’ajouter des fonctionnalités inutiles, mais de construire
                  une solution claire, agréable et réellement utilisée.
                </p>
              </div>
            </Reveal>

            <div className="mt-9 grid items-start gap-7 md:grid-cols-2">
              {aboutServices.map((service) => (
                <Reveal key={service.id}>
                  <ServiceVisualCard service={service} />
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-16 lg:mt-20">
              <div className="border-y border-slate-300/70 py-6 dark:border-white/10">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                  J’accompagne notamment
                </p>

                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
                  {aboutAudiences.map((audience, index) => (
                    <React.Fragment key={audience}>
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                        {audience}
                      </span>

                      {index < aboutAudiences.length - 1 ? (
                        <span
                          className="hidden text-[#ff7900] sm:inline"
                          aria-hidden="true"
                        >
                          •
                        </span>
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-20">
          <div className="border-y border-slate-300/70 dark:border-white/10">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4">
              {aboutCommitments.map((item, index) => (
                <CommitmentItem key={item.title} item={item} index={index} />
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-14">
          <div className="relative overflow-hidden rounded-[22px] bg-[#0868f2] px-6 py-9 text-white sm:px-9 lg:px-12 lg:py-11">
            <div className="pointer-events-none absolute -right-12 -top-20 h-64 w-64 rounded-full border-[45px] border-white/10" />

            <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">
                  Un projet en tête ?
                </p>

                <h3 className="mt-3 max-w-3xl text-3xl font-semibold leading-tight tracking-[-0.03em] sm:text-4xl">
                  Parlons simplement de votre besoin et imaginons la bonne
                  solution.
                </h3>
              </div>

              <a
                href="#contact"
                className="group inline-flex h-12 shrink-0 items-center justify-center gap-3 self-start rounded-full bg-white px-6 text-sm font-semibold text-[#0868f2] transition hover:bg-slate-50 lg:self-auto"
              >
                Présenter mon projet
                <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900] text-white">
                  <FaArrowRight className="text-[9px] transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
